import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCreateCase } from '../hooks/useCases';
import { Button, Card, Input } from '../components/ui';

const lastWord = (s: string) => s.trim().split(/\s+/).pop() || '';

export default function NewCasePage() {
  const navigate = useNavigate();
  const createCase = useCreateCase();

  const [clientName, setClientName] = useState('');
  const [opposingParty, setOpposingParty] = useState('');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);

  // Suggest "Smith v. Smith" from the party names until the user edits
  // the case name themselves.
  useEffect(() => {
    if (nameTouched) return;
    const a = lastWord(clientName);
    const b = lastWord(opposingParty);
    if (a && b) setName(`${a} v. ${b}`);
  }, [clientName, opposingParty, nameTouched]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const result = await createCase.mutateAsync({
      name: name.trim(),
      client_name: clientName.trim() || undefined,
      opposing_party_name: opposingParty.trim() || undefined,
    });

    navigate(`/cases/${result.public_id}`);
  };

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <Button
        variant="ghost"
        icon={ArrowLeft}
        onClick={() => navigate(-1)}
        className="mb-6 -ml-3 text-green-700 hover:text-green-900"
      >
        Back
      </Button>

      <h1 className="t-title text-green-900 mb-8">New Case</h1>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Client Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="e.g., Jane Smith"
              autoFocus
            />
            <Input
              label="Opposing Party"
              value={opposingParty}
              onChange={(e) => setOpposingParty(e.target.value)}
              placeholder="e.g., John Smith"
            />
          </div>

          <Input
            label="Case Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameTouched(true);
            }}
            placeholder="e.g., Smith v. Smith"
            required
          />

          <p className="text-sm text-green-700">
            That's all you need to get started. Court, cause number, key dates,
            and opposing counsel can be added anytime under the case's{' '}
            <span className="font-medium">Case Info</span> tab.
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" size="lg" type="button" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button size="lg" type="submit" loading={createCase.isPending}>
              Create Case
            </Button>
          </div>

          {createCase.isError && (
            <p className="text-sm text-danger mt-2">
              Failed to create case. Please try again.
            </p>
          )}
        </form>
      </Card>
    </div>
  );
}
